import axios from 'axios';
import * as XLSX from 'xlsx';

const BASE_URL = process.env.REACT_APP_BACKEND_URI;

const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
    'Content-Type': 'application/json',
});

const handleApiError = (error, defaultMessage) => {
    if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
    }
    throw new Error(defaultMessage);
};

export const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const lines = text.split(/\r?\n/);
                const appNames = [];
                
                lines.forEach((line) => {
                    const values = line.split(',');
                    values.forEach((value) => {
                        const trimmed = value.trim().toUpperCase();
                        if (trimmed && !appNames.includes(trimmed)) {
                            appNames.push(trimmed);
                        }
                    });
                });
                
                resolve(appNames);
            } catch (error) {
                reject(new Error('Failed to parse CSV file'));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsText(file);
    });
};

export const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const appNames = [];
                
                workbook.SheetNames.forEach((sheetName) => {
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    jsonData.forEach((row) => {
                        row.forEach((cell) => {
                            if (cell) {
                                const trimmed = String(cell).trim().toUpperCase();
                                if (trimmed && !appNames.includes(trimmed)) {
                                    appNames.push(trimmed);
                                }
                            }
                        });
                    });
                });
                
                resolve(appNames);
            } catch (error) {
                reject(new Error('Failed to parse Excel file'));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsArrayBuffer(file);
    });
};

export const parseCommaSeparatedInput = (input) => {
    if (!input || typeof input !== 'string') return [];
    
    const values = input.split(',');
    const appNames = [];
    
    values.forEach((value) => {
        const trimmed = value.trim().toUpperCase();
        if (trimmed && !appNames.includes(trimmed)) {
            appNames.push(trimmed);
        }
    });
    
    return appNames;
};

export const generateListName = (existingCount, userName) => {
    const date = new Date();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const timestamp = Date.now();
    
    const firstName = userName ? userName.split(' ')[0] : 'User';
    
    return `${firstName}_List_${existingCount + 1}_${month}_${day}_${timestamp}`;
};

export const validateApplications = async (appNames) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/subscription_lists/validateApplications`,
            { app_names: appNames },
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to validate applications');
    }
};

export const createSubscriptionList = async ({ userName, userEmail, listName, appNames, description }) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/subscription_lists/create`,
            {
                user_name: userName,
                user_email: userEmail,
                list_name: listName,
                app_names: appNames,
                description: description || null,
            },
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to create subscription list');
    }
};

export const getUserLists = async (userEmail) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/api/subscription_lists/getDetails`,
            {
                params: { user_email: userEmail },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch subscription lists');
    }
};

export const getListDetails = async (userEmail, listId) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/api/subscription_lists/getDetails`,
            {
                params: { user_email: userEmail, list_id: listId },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch list details');
    }
};

export const updateListMetadata = async ({ userEmail, listId, listName, description }) => {
    try {
        const payload = { user_email: userEmail, list_id: listId };
        if (listName !== undefined) payload.list_name = listName;
        if (description !== undefined) payload.description = description;

        const response = await axios.put(
            `${BASE_URL}/api/subscription_lists/update`,
            payload,
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to update list');
    }
};

export const deleteSubscriptionList = async (userEmail, listId) => {
    try {
        const response = await axios.delete(
            `${BASE_URL}/api/subscription_lists/delete`,
            {
                data: { user_email: userEmail, list_id: listId },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to delete list');
    }
};

export const cloneSubscriptionList = async ({ userName, userEmail, listId, newListName }) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/subscription_lists/clone`,
            {
                user_name: userName,
                user_email: userEmail,
                list_id: listId,
                new_list_name: newListName,
            },
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to clone list');
    }
};

export const addAppsToList = async ({ userName, userEmail, listId, appNames }) => {
    try {
        const response = await axios.put(
            `${BASE_URL}/api/subscription_lists/addApps`,
            {
                user_name: userName,
                user_email: userEmail,
                list_id: listId,
                app_names: appNames,
            },
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to add applications to list');
    }
};

export const removeAppsFromList = async ({ userName, userEmail, listId, appNames }) => {
    try {
        const response = await axios.delete(
            `${BASE_URL}/api/subscription_lists/removeApps`,
            {
                data: {
                    user_name: userName,
                    user_email: userEmail,
                    list_id: listId,
                    app_names: appNames,
                },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to remove applications from list');
    }
};

export const shareList = async ({ userName, userEmail, listId, shareWithRead = [], shareWithWrite = [] }) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/subscription_lists/share`,
            {
                user_name: userName,
                user_email: userEmail,
                list_id: listId,
                share_with_read: shareWithRead,
                share_with_write: shareWithWrite,
            },
            { headers: getAuthHeaders() }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to share list');
    }
};

export const revokeAccess = async ({ ownerEmail, listId, revokeEmails }) => {
    try {
        const response = await axios.delete(
            `${BASE_URL}/api/subscription_lists/revokeAccess`,
            {
                data: {
                    user_email: ownerEmail,
                    list_id: listId,
                    revoke_emails: revokeEmails,
                },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to revoke access');
    }
};

export const getListChangesOverview = async (userEmail, listId) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/api/subscription-list/getChangesOverview`,
            {
                params: { user_email: userEmail, list_id: listId },
                headers: getAuthHeaders(),
            }
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch changes overview');
    }
};

export default {
    parseCSVFile,
    parseExcelFile,
    parseCommaSeparatedInput,
    generateListName,
    validateApplications,
    createSubscriptionList,
    getUserLists,
    getListDetails,
    updateListMetadata,
    deleteSubscriptionList,
    cloneSubscriptionList,
    addAppsToList,
    removeAppsFromList,
    shareList,
    revokeAccess,
    getListChangesOverview,
};
